/**
 * Proportional scroll mapping for large datasets.
 *
 * When the total pixel height (totalRows * rowHeight) would exceed the browser's
 * max scroll height (~33M px in Chrome), we cap the virtual height and map scroll
 * position proportionally to row indices. This allows displaying millions of rows
 * without any browser overflow.
 */

/**
 * Max virtual height in pixels. Browsers cap at ~33.5M (Chrome) / ~17.9M (Firefox).
 * We use 10M to stay well within limits with margin.
 */
export const MAX_VIRTUAL_HEIGHT = 10_000_000;

/** Default row height in pixels — must match ResultsGrid's ROW_HEIGHT */
export const ROW_HEIGHT = 32;

/** Threshold: if totalRows * ROW_HEIGHT exceeds this, use proportional mode */
export const PROPORTIONAL_THRESHOLD = MAX_VIRTUAL_HEIGHT;

/** Whether proportional scrolling is needed for the given row count */
export function needsProportionalScroll(totalRows: number): boolean {
  return totalRows * ROW_HEIGHT > PROPORTIONAL_THRESHOLD;
}

/**
 * Convert a native scrollTop value to a first-visible row index.
 * In proportional mode, the scroll range is capped and we map linearly.
 * In normal mode, this is just `scrollTop / rowHeight`.
 */
export function scrollTopToRowIndex(
  scrollTop: number,
  totalRows: number,
  containerHeight: number,
): number {
  if (totalRows <= 0 || containerHeight <= 0) return 0;

  const visibleRows = Math.ceil(containerHeight / ROW_HEIGHT);

  if (!needsProportionalScroll(totalRows)) {
    // Normal mode — direct pixel mapping
    return Math.floor(scrollTop / ROW_HEIGHT);
  }

  // Proportional mode — map scroll position to row index
  const maxScrollable = totalRows - visibleRows;
  if (maxScrollable <= 0) return 0;

  const virtualHeight = getVirtualHeight(totalRows, containerHeight);
  const maxScrollTop = virtualHeight - containerHeight;
  if (maxScrollTop <= 0) return 0;

  const ratio = Math.min(scrollTop / maxScrollTop, 1);
  return Math.round(ratio * maxScrollable);
}

/**
 * Convert a row index to the corresponding scrollTop value.
 * Inverse of scrollTopToRowIndex.
 */
export function rowIndexToScrollTop(
  rowIndex: number,
  totalRows: number,
  containerHeight: number,
): number {
  if (totalRows <= 0 || containerHeight <= 0) return 0;

  const visibleRows = Math.ceil(containerHeight / ROW_HEIGHT);

  if (!needsProportionalScroll(totalRows)) {
    return rowIndex * ROW_HEIGHT;
  }

  const maxScrollable = totalRows - visibleRows;
  if (maxScrollable <= 0) return 0;

  const virtualHeight = getVirtualHeight(totalRows, containerHeight);
  const maxScrollTop = virtualHeight - containerHeight;
  if (maxScrollTop <= 0) return 0;

  const ratio = Math.min(rowIndex / maxScrollable, 1);
  return Math.round(ratio * maxScrollTop);
}

/**
 * Get the total virtual height to use as the scrollable area.
 * Capped at MAX_VIRTUAL_HEIGHT in proportional mode.
 */
export function getVirtualHeight(
  totalRows: number,
  containerHeight: number,
): number {
  const naturalHeight = totalRows * ROW_HEIGHT;
  if (naturalHeight <= MAX_VIRTUAL_HEIGHT) return naturalHeight;
  // In proportional mode, use the max height (must be >= containerHeight)
  return Math.max(MAX_VIRTUAL_HEIGHT, containerHeight);
}

/**
 * Calculate the top and bottom padding heights for the virtualized table.
 * In proportional mode, these are derived from scroll ratio rather than pixel positions.
 *
 * TODO: PageDown/PageUp in proportional mode jumps by ~192 rows at 3M total (browser
 * default scroll distance mapped through the proportional ratio). Consider intercepting
 * keyboard events to advance by exactly one viewport height instead. Add unit tests
 * for PageDown/PageUp mapping once the behavior is changed.
 */
export function getVirtualPadding(
  scrollTop: number,
  totalRows: number,
  containerHeight: number,
  overscan: number,
): {
  startIndex: number;
  endIndex: number;
  topPaddingHeight: number;
  bottomPaddingHeight: number;
} {
  if (totalRows <= 0) {
    return { startIndex: 0, endIndex: 0, topPaddingHeight: 0, bottomPaddingHeight: 0 };
  }

  const effectiveHeight = containerHeight || 600;
  const firstVisibleRow = scrollTopToRowIndex(scrollTop, totalRows, effectiveHeight);
  const visibleRowCount = Math.ceil(effectiveHeight / ROW_HEIGHT);

  const start = Math.max(0, firstVisibleRow - overscan);
  const end = Math.min(totalRows, firstVisibleRow + visibleRowCount + overscan);

  if (!needsProportionalScroll(totalRows)) {
    // Normal mode — use real pixel heights
    return {
      startIndex: start,
      endIndex: end,
      topPaddingHeight: start * ROW_HEIGHT,
      bottomPaddingHeight: Math.max(0, (totalRows - end) * ROW_HEIGHT),
    };
  }

  // Proportional mode — distribute the virtual height proportionally
  const virtualHeight = getVirtualHeight(totalRows, effectiveHeight);
  const renderedHeight = (end - start) * ROW_HEIGHT;
  const remainingHeight = Math.max(0, virtualHeight - renderedHeight);

  // Distribute remaining height between top and bottom proportionally
  const topRatio = totalRows > 0 ? start / totalRows : 0;
  const bottomRows = totalRows - end;
  const bottomRatio = totalRows > 0 ? bottomRows / totalRows : 0;
  const totalRatio = topRatio + bottomRatio || 1;

  return {
    startIndex: start,
    endIndex: end,
    topPaddingHeight: Math.round(remainingHeight * (topRatio / totalRatio)),
    bottomPaddingHeight: Math.round(remainingHeight * (bottomRatio / totalRatio)),
  };
}
